import React, { useState, useCallback } from 'react';
import { FileCheck, X, CloudUpload, FolderOpen, Globe, Rocket } from 'lucide-react';
import { VALID_FILE_EXTENSIONS, MAX_FILE_SIZE_BYTES, AMAZON_REGIONS, BULK_UPLOAD_CARD_STRINGS } from '../../constants/appConstants';
import styles from './BulkUploadCard.module.scss';

const BulkUploadCard = ({ 
  onBulkUpload,
  bulkLoading,
  selectedFile,
  setSelectedFile,
  error,
  setError 
}) => {
  const [bulkAmazonCountry, setBulkAmazonCountry] = useState('amazon.com');
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFileSelection = useCallback((file) => {
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!VALID_FILE_EXTENSIONS.includes(fileExtension)) {
      setError(BULK_UPLOAD_CARD_STRINGS.ERRORS.INVALID_FORMAT);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(BULK_UPLOAD_CARD_STRINGS.ERRORS.FILE_TOO_LARGE);
      return;
    }

    setSelectedFile(file);
    setError('');
  }, [setError, setSelectedFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFileSelection(file);
    }
  }, [handleFileSelection]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const handleUpload = () => {
    onBulkUpload(bulkAmazonCountry);
  };

  return (
    <div className={styles.bulkUploadCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderContent}>
          <h3 className={styles.cardTitle}>{BULK_UPLOAD_CARD_STRINGS.TITLE}</h3>
          <p className={styles.cardDescription}>{BULK_UPLOAD_CARD_STRINGS.DESCRIPTION}</p>
        </div>
      </div>

      <div className={styles.cardBody}>
        <div 
          className={`${styles.uploadZone} ${dragActive ? styles.dragActive : ''} ${selectedFile ? styles.hasFile : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('fileInput').click()}
        >
          <input
            type="file"
            id="fileInput"
            className={styles.fileInput}
            accept=".csv,.xlsx,.xls"
            onChange={handleFileInput}
          />
          
          {selectedFile ? (
            <div className={styles.filePreview}>
              <div className={`${styles.fileIcon} ${styles.success}`}>
                <FileCheck size={16} />
              </div>
              <div className={styles.fileInfo}>
                <div className={styles.fileName}>{selectedFile.name}</div>
                <div className={styles.fileDetails}>
                  <span className={styles.fileSize}>
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                  <span className={styles.fileType}>
                    {selectedFile.name.split('.').pop().toUpperCase()}
                  </span>
                </div>
              </div>
              <button 
                className={styles.fileRemove}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className={styles.uploadContent}>
              <div className={styles.uploadIcon}>
                <CloudUpload size={24} />
              </div>
              <h4 className={styles.uploadTitle}>{BULK_UPLOAD_CARD_STRINGS.UPLOAD.TITLE}</h4>
              <p className={styles.uploadDescription}>
                {BULK_UPLOAD_CARD_STRINGS.UPLOAD.DESCRIPTION}
              </p>
              <button className={styles.browseButton} type="button">
                <FolderOpen size={14} />
                {BULK_UPLOAD_CARD_STRINGS.UPLOAD.BROWSE}
              </button>
            </div>
          )}
        </div>

        {/* Amazon Region for Bulk Upload */}
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            <Globe size={14} />
            {BULK_UPLOAD_CARD_STRINGS.UPLOAD.AMAZON_REGION}
          </label>
          <div className={styles.formInputWrapper}>
            <select
              name="amazon_country"
              className={styles.formSelect}
              value={bulkAmazonCountry}
              onChange={(e) => setBulkAmazonCountry(e.target.value)}
            >
              {AMAZON_REGIONS.map(region => (
                <option key={region.value} value={region.value}>{region.label}</option>
              ))}
            </select>
          </div>
          <div className={styles.formHelp}>
            {BULK_UPLOAD_CARD_STRINGS.UPLOAD.AMAZON_HELP}
          </div>
        </div>

        <div className={styles.uploadGuide}>
          <h5 className={styles.guideTitle}>
            {BULK_UPLOAD_CARD_STRINGS.GUIDE.TITLE}
          </h5>
          <ul className={styles.guideList}>
            {BULK_UPLOAD_CARD_STRINGS.GUIDE.ITEMS.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>

        <button 
          className={styles.uploadButton}
          onClick={handleUpload}
          disabled={!selectedFile || bulkLoading}
        >
          {bulkLoading ? (
            <>
              <div className={styles.btnSpinner}></div>
              <span>{BULK_UPLOAD_CARD_STRINGS.BUTTONS.PROCESSING}</span>
            </>
          ) : (
            <>
              <Rocket size={14} />
              <span>{BULK_UPLOAD_CARD_STRINGS.BUTTONS.ANALYZE}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BulkUploadCard;
